import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, type ApiError } from '@/lib/api';
import { toast } from '@/lib/toast';
import { blockKeys } from '@/features/calendars/useTimeBlocks';

export type WorkBlockStatus = 'scheduled' | 'active' | 'completed' | 'skipped';
export type WorkBlockPhase =
  | 'planning'
  | 'sprint'
  | 'break_pending'
  | 'break'
  | 'return_pending'
  | 'wind_down'
  | 'review';
export type WorkBlockOrigin = 'daily_start' | 'plan_scheduled' | 'ad_hoc';

export interface WorkBlockItem {
  id: number;
  task_id: number | null;
  calendar_event_id: number | null;
  time_block_id: number | null;
  estimated_minutes: number | null;
  sort_order: number;
  completed_at: string | null;
  carried_from_block_id: number | null;
  required_meeting: boolean;
  skipped: boolean;
  title: string | null;
}

export interface WorkBlockSuggestion {
  task_id: number | null;
  calendar_event_id: number | null;
  time_block_id: number | null;
  title: string;
  estimated_minutes: number;
  required_meeting: boolean;
  pull_forward?: boolean;
  client_match?: boolean;
}

export interface UnlabeledClientEntry {
  id: number;
  client_id: number;
  client_name: string;
  duration_seconds: number;
  note: string | null;
}

/** One line of the block's running tab, what the time actually went to. */
export interface BlockActivity {
  key: string;
  title: string;
  kind: 'task' | 'meeting' | 'client';
  task_id: number | null;
  /** Closed time only; add the live remainder from `running_since`. */
  seconds: number;
  running_since: string | null;
  completed: boolean;
}

export interface WorkBlock {
  id: number;
  starts_at: string;
  ends_at: string;
  status: WorkBlockStatus;
  phase: WorkBlockPhase;
  sprint_index: number;
  planning_ends_at: string | null;
  wind_down_ends_at: string | null;
  wind_down_deducted_seconds: number;
  light_day: boolean;
  ended_early: boolean;
  origin: WorkBlockOrigin;
  client_id: number | null;
  project_id: number | null;
  items: WorkBlockItem[];
  sprint_budget_minutes: number;
  selected_minutes: number;
  unlabeled_client_entries: UnlabeledClientEntry[];
  activity: BlockActivity[];
}

const WORK_BLOCKS_KEY = ['work-blocks'] as const;

export function workBlocksKey(date?: string) {
  return [...WORK_BLOCKS_KEY, date ?? 'today'] as const;
}

export function useWorkBlocks(date?: string) {
  const qs = date ? `?date=${encodeURIComponent(date)}` : '';
  return useQuery<WorkBlock[]>({
    queryKey: workBlocksKey(date),
    queryFn: () => api<WorkBlock[]>(`/api/work-blocks${qs}`),
    refetchInterval: 15_000,
  });
}

export function useWorkBlock(id: number | null) {
  return useQuery<WorkBlock>({
    queryKey: [...WORK_BLOCKS_KEY, 'detail', id],
    queryFn: () => api<WorkBlock>(`/api/work-blocks/${id}`),
    enabled: id != null,
    refetchInterval: 5_000,
  });
}

export function useWorkBlockSuggestions(blockId: number | null) {
  return useQuery<WorkBlockSuggestion[]>({
    queryKey: [...WORK_BLOCKS_KEY, 'suggestions', blockId],
    queryFn: () => api<WorkBlockSuggestion[]>(`/api/work-blocks/${blockId}/suggestions`),
    enabled: blockId != null,
  });
}

export function useWorkBlockControls() {
  const qc = useQueryClient();

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: WORK_BLOCKS_KEY });
    void qc.invalidateQueries({ queryKey: ['focus'] });
    void qc.invalidateQueries({ queryKey: blockKeys.all });
  };

  const post = (path: string, body?: unknown) =>
    api<WorkBlock>(path, { method: 'POST', json: body ?? {} });

  const patch = (path: string, body: unknown) =>
    api<WorkBlock>(path, { method: 'PATCH', json: body });

  return {
    create: useMutation<WorkBlock, ApiError, { starts_at?: string; light_day?: boolean; origin?: WorkBlockOrigin }>({
      mutationFn: (body) => api<WorkBlock>('/api/work-blocks', { method: 'POST', json: body }),
      onSuccess: invalidate,
      onError: (e) => toast.error(e.message),
    }),
    start: useMutation<WorkBlock, ApiError, number>({
      mutationFn: (blockId) => post(`/api/work-blocks/${blockId}/start`),
      onSuccess: invalidate,
      onError: (e) => toast.error(e.message),
    }),
    updateItems: useMutation<WorkBlock, ApiError, { blockId: number; items: Partial<WorkBlockItem>[] }>({
      mutationFn: ({ blockId, items }) =>
        patch(`/api/work-blocks/${blockId}/items`, items),
      onSuccess: invalidate,
      onError: (e) => toast.error(e.message),
    }),
    addItem: useMutation<
      WorkBlock,
      ApiError,
      {
        blockId: number;
        task_id?: number | null;
        calendar_event_id?: number | null;
        time_block_id?: number | null;
        estimated_minutes?: number | null;
        required_meeting?: boolean;
      }
    >({
      mutationFn: ({ blockId, ...body }) =>
        api<WorkBlock>(`/api/work-blocks/${blockId}/items`, { method: 'POST', json: body }),
      onSuccess: invalidate,
      onError: (e) => toast.error(e.message),
    }),
    confirmPlanning: useMutation<WorkBlock, ApiError, number>({
      mutationFn: (blockId) => post(`/api/work-blocks/${blockId}/confirm-planning`),
      onSuccess: invalidate,
      onError: (e) => toast.error(e.message),
    }),
    confirmBreak: useMutation<WorkBlock, ApiError, number>({
      mutationFn: (blockId) => post(`/api/work-blocks/${blockId}/confirm-break`),
      onSuccess: invalidate,
      onError: (e) => toast.error(e.message),
    }),
    endBreak: useMutation<WorkBlock, ApiError, number>({
      mutationFn: (blockId) => post(`/api/work-blocks/${blockId}/end-break`),
      onSuccess: invalidate,
      onError: (e) => toast.error(e.message),
    }),
    confirmReturn: useMutation<
      WorkBlock,
      ApiError,
      { blockId: number; task_id?: number }
    >({
      mutationFn: ({ blockId, ...body }) =>
        post(`/api/work-blocks/${blockId}/confirm-return`, body),
      onSuccess: invalidate,
      onError: (e) => toast.error(e.message),
    }),
    extend: useMutation<WorkBlock, ApiError, { blockId: number; minutes: number }>({
      mutationFn: ({ blockId, minutes }) =>
        post(`/api/work-blocks/${blockId}/extend`, { minutes }),
      onSuccess: invalidate,
      onError: (e) => toast.error(e.message),
    }),
    pauseTask: useMutation<void, ApiError, number>({
      mutationFn: (blockId) =>
        api(`/api/work-blocks/${blockId}/pause-task`, { method: 'POST' }),
      onSuccess: invalidate,
      onError: (e) => toast.error(e.message),
    }),
    endEarly: useMutation<WorkBlock, ApiError, number>({
      mutationFn: (blockId) => post(`/api/work-blocks/${blockId}/end-early`),
      onSuccess: invalidate,
      onError: (e) => toast.error(e.message),
    }),
    cancel: useMutation<WorkBlock, ApiError, number>({
      mutationFn: (blockId) => post(`/api/work-blocks/${blockId}/cancel`),
      onSuccess: invalidate,
      onError: (e) => toast.error(e.message),
    }),
    finish: useMutation<
      WorkBlock,
      ApiError,
      {
        blockId: number;
        carry_item_ids?: number[];
        label_client_entries?: { entry_id: number; title: string }[];
      }
    >({
      mutationFn: ({ blockId, ...body }) =>
        post(`/api/work-blocks/${blockId}/finish`, body),
      onSuccess: invalidate,
      onError: (e) => toast.error(e.message),
    }),
    captureTask: useMutation<
      WorkBlock,
      ApiError,
      {
        blockId: number;
        title: string;
        client_id?: number;
        estimate_minutes?: number;
      }
    >({
      mutationFn: ({ blockId, ...body }) =>
        post(`/api/work-blocks/${blockId}/capture-task`, body),
      onSuccess: invalidate,
      onError: (e) => toast.error(e.message),
    }),
  };
}
